import { Hero } from '@/components/pet/Hero';
import { Reviews } from '@/components/pet/Reviews';
import { PageWrapper } from '@/components/shared/Content';
import Layout from '@/components/shared/Layout';
import { SectionEventWrapper } from '@/components/shared/SectionEventWrapper';
import { SEO } from '@/components/shared/SEO';
import { GEO } from '@/const/cookies';
import type { Config } from '@/pageConfig/pet/pet';
import { PET_LANDING_PAGE } from '@/utils/productAdaPages';
import { PRODUCT_PET } from '@/utils/products';
import { useAnalyticsContext } from '@lemonade-hq/nuiza';
import dynamic from 'next/dynamic';
import { rgba } from 'polished';
import { useEffect } from 'react';
import { useCookies } from 'react-cookie';
import styled from 'styled-components';

const Doggy = dynamic(() => import('@/components/pet/Doggy').then(mod => mod.Doggy));
const Publications = dynamic(() => import('@/components/pet/Publications'));
const AppsSection = dynamic(import('@/components/pet/AppsSection'));
const Coverage = dynamic(import('@/components/pet/Coverage/Coverage').then(mod => mod.Coverage));
const PetSaveSection = dynamic(import('@/components/pet/PetSaveSection/PetSaveSection'));
const PuppyKitten = dynamic(import('@/components/pet/PuppyKitten/PuppyKitten'));
const GoodToKnowSection = dynamic(import('@/components/pet/GoodToKnowSection/GoodToKnowSection'));
const PetGoodSection = dynamic(import('@/components/pet/PetGoodSection').then(mod => mod.PetGoodSection));

interface PetPageTemplateProps {
  config: Config;
}

const StyledFooterLink = styled.a`
  color: ${({ theme }) => rgba(theme.colors.metal, 0.5)};
  text-decoration: underline;
`;

const DISCLOSURE_LINKS = {
  LEM_PET_INFO_0923: 'https://icebox.lemonade.com/tokens/lftkn-nxdg3g6z7g5yok5nen00?inline=true',
  LEM_PET_INFO_1223: 'https://icebox.lemonade.com/tokens/lftkn-xh8c-fycx4j5g-ww5qf2?inline=true',
  LPC_PET_INFO_1223: 'https://icebox.lemonade.com/tokens/lftkn-zvosvza47ppjfl8mfh1z?inline=true',
  DE: 'https://icebox.lemonade.com/tokens/lftkn-j-shlq8ncyhvbtregin2?inline=true',
  IL: 'https://icebox.lemonade.com/tokens/lftkn-gma6z9cwdmvtz50dtzff?inline=true',
  OH: 'https://icebox.lemonade.com/tokens/lftkn-e3rebivhp4z-yloore1z?inline=true',
  LA: 'https://icebox.lemonade.com/tokens/lftkn-38cnfnt14ikt7wdnc8nc?inline=true',
};

const PetStateDisclosures = () => {
  return (
    <p>
      Lemonade Pet State Disclosures:{' '}
      <StyledFooterLink href={DISCLOSURE_LINKS.IL} target="_blank">
        Illinois
      </StyledFooterLink>
      ,{' '}
      <StyledFooterLink href={DISCLOSURE_LINKS.LEM_PET_INFO_0923} target="_blank">
        California
      </StyledFooterLink>
      ,{' '}
      <StyledFooterLink href={DISCLOSURE_LINKS.LEM_PET_INFO_0923} target="_blank">
        Mississippi
      </StyledFooterLink>
      ,{' '}
      <StyledFooterLink href={DISCLOSURE_LINKS.LEM_PET_INFO_0923} target="_blank">
        New Hampshire
      </StyledFooterLink>
      ,{' '}
      <StyledFooterLink href={DISCLOSURE_LINKS.LEM_PET_INFO_0923} target="_blank">
        Washington
      </StyledFooterLink>
      ,{' '}
      <StyledFooterLink href={DISCLOSURE_LINKS.OH} target="_blank">
        Ohio
      </StyledFooterLink>
      ,{' '}
      <StyledFooterLink href={DISCLOSURE_LINKS.LEM_PET_INFO_0923} target="_blank">
        Pennsylvania
      </StyledFooterLink>
      ,{' '}
      <StyledFooterLink href={DISCLOSURE_LINKS.LEM_PET_INFO_0923} target="_blank">
        Nebraska
      </StyledFooterLink>
      ,{' '}
      <StyledFooterLink href={DISCLOSURE_LINKS.LEM_PET_INFO_0923} target="_blank">
        Maryland
      </StyledFooterLink>
      ,{' '}
      <StyledFooterLink href={DISCLOSURE_LINKS.LA} target="_blank">
        Louisiana
      </StyledFooterLink>
      ,{' '}
      <StyledFooterLink href={DISCLOSURE_LINKS.LEM_PET_INFO_1223} target="_blank">
        Montana
      </StyledFooterLink>
      ,{' '}
      <StyledFooterLink href={DISCLOSURE_LINKS.LEM_PET_INFO_1223} target="_blank">
        Florida
      </StyledFooterLink>
      ,{' '}
      <StyledFooterLink href={DISCLOSURE_LINKS.LEM_PET_INFO_1223} target="_blank">
        Rhode Island
      </StyledFooterLink>
      ,{' '}
      <StyledFooterLink href={DISCLOSURE_LINKS.LPC_PET_INFO_1223} target="_blank">
        Hawaii
      </StyledFooterLink>
      ,{' '}
      <StyledFooterLink href={DISCLOSURE_LINKS.DE} target="_blank">
        Delaware
      </StyledFooterLink>
    </p>
  );
};

export const PetPageTemplate: React.FC<PetPageTemplateProps> = ({ config }) => {
  const [cookies] = useCookies([GEO]);
  const { trackEvent } = useAnalyticsContext();

  const { country, region } = cookies[GEO] || {};

  // biome-ignore lint/correctness/useExhaustiveDependencies: trackEvent is causing a double firing of the event
  useEffect(() => {
    if (country === 'US' && region === '') trackEvent('pet_landing_page.us_unidentified_region');
  }, [country, region]);

  return (
    <Layout
      headerActions={config.layout.headerActions}
      product={PRODUCT_PET}
      page={PET_LANDING_PAGE}
      productType={PRODUCT_PET}
      alignLogoOnMobile={config.layout.alignLogoOnMobile}
      extraFooterDisclaimer={<PetStateDisclosures />}
    >
      <SEO {...config.seo} />
      <PageWrapper>
        <SectionEventWrapper name="hero" page="pet">
          <Hero {...config.hero} />
        </SectionEventWrapper>
        <SectionEventWrapper name="publications" page="pet">
          <Publications {...config.publications} />
        </SectionEventWrapper>
        <SectionEventWrapper name="appSection" page="pet">
          <AppsSection pt={0} />
        </SectionEventWrapper>
        <SectionEventWrapper name="reviews" page="pet">
          <Reviews />
        </SectionEventWrapper>
        <SectionEventWrapper name="coverage" page="pet">
          <Coverage />
        </SectionEventWrapper>
        <SectionEventWrapper name="petSaveSection" page="pet">
          <PetSaveSection />
        </SectionEventWrapper>
        <SectionEventWrapper name="puppyKitten" page="pet">
          <PuppyKitten />
        </SectionEventWrapper>
        <SectionEventWrapper name="goodToKnowSection" page="pet">
          <GoodToKnowSection />
        </SectionEventWrapper>
        <Doggy />
        <SectionEventWrapper name="petGoodSection" page="pet">
          <PetGoodSection />
        </SectionEventWrapper>
      </PageWrapper>
    </Layout>
  );
};
